<template>
  <Teleport to="body">
    <div
      class="setup-wizard"
      role="dialog"
      aria-modal="true"
      :aria-label="$t('courseSetup.title')"
      @keydown.escape.stop="onEscape"
      @keydown.tab="trapFocus"
    >
      <div ref="card" class="wizard-card" tabindex="-1">
        <header class="wizard-header">
          <div class="header-row">
            <button
              class="back-btn"
              data-test="wizard-back"
              :aria-label="currentStep === 1 ? $t('common.close') : $t('courseSetup.back')"
              :disabled="phase !== 'idle'"
              @click="goBack"
            >
              <font-awesome-icon
                :icon="currentStep === 1 ? 'fa-solid fa-xmark' : 'fa-solid fa-chevron-left'"
              />
            </button>
            <span v-if="phase !== 'success'" class="step-label u-tabular">
              {{ $t('courseSetup.stepOf', { current: currentStep, total: totalSteps }) }}
            </span>
          </div>
          <h2 class="wizard-title">{{ $t('courseSetup.title') }}</h2>
          <p class="wizard-intro">{{ $t('courseSetup.intro') }}</p>
          <div class="progress-track" aria-hidden="true">
            <span
              v-for="s in totalSteps"
              :key="s"
              :class="['progress-segment', { filled: s <= currentStep }]"
            ></span>
          </div>
        </header>

        <!-- Success state replaces the step flow after a successful save. -->
        <div v-if="phase === 'success'" class="wizard-success" data-test="wizard-success">
          <div class="success-icon">
            <font-awesome-icon icon="check" />
          </div>
          <h3 class="success-title">{{ $t('courseSetup.successTitle') }}</h3>
          <p class="success-body">{{ $t('courseSetup.successBody') }}</p>
        </div>

        <template v-else>
          <Transition :name="stepTransition" mode="out-in">
            <div :key="currentStep" class="step-body">
              <!-- Step 1: Goals -->
              <template v-if="currentStep === 1">
                <h3 ref="stepTitle" tabindex="-1" class="step-title">
                  🎯 {{ $t('courseSetup.steps.goals') }}
                </h3>
                <div class="input-section">
                  <label :id="ids.specificGoals">{{ $t('courseSetup.goalsLabel') }}</label>
                  <div
                    class="pill-group"
                    role="group"
                    :aria-labelledby="ids.specificGoals"
                    :aria-invalid="shouldShowError('specificGoals')"
                  >
                    <button
                      v-for="goal in specificGoalsOptions"
                      :key="goal.value"
                      type="button"
                      :class="['pill-button', { active: selections.specificGoals.includes(goal.value) }]"
                      :data-test="'goal-' + goal.value"
                      @click="toggleMulti(selections.specificGoals, goal.value)"
                    >
                      {{ $t('courseSetup.goalsOptions.' + goal.value) }}
                    </button>
                  </div>
                  <p v-if="shouldShowError('specificGoals')" class="error-message" role="alert">
                    {{ $t('courseSetup.errors.goals') }}
                  </p>
                </div>
                <div v-if="selections.specificGoals.length > 0" class="input-section">
                  <label :for="ids.goalsDetails">{{ $t('courseSetup.goalsDetailsLabel') }}</label>
                  <textarea
                    :id="ids.goalsDetails"
                    v-model="selections.specificGoalsDetails"
                    class="text-input"
                    rows="2"
                    maxlength="200"
                    :placeholder="$t('courseSetup.goalsPlaceholder')"
                    :aria-invalid="shouldShowError('specificGoalsDetails')"
                  ></textarea>
                  <p
                    v-if="shouldShowError('specificGoalsDetails')"
                    class="error-message"
                    role="alert"
                  >
                    {{ $t('courseSetup.errors.goalsDetails') }}
                  </p>
                </div>
              </template>

              <!-- Step 2: Schedule -->
              <template v-else-if="currentStep === 2">
                <h3 ref="stepTitle" tabindex="-1" class="step-title">
                  📅 {{ $t('courseSetup.steps.schedule') }}
                </h3>
                <div class="input-section">
                  <label :id="ids.courseLength">{{ $t('courseSetup.courseLengthLabel') }}</label>
                  <div
                    class="pill-group"
                    role="group"
                    :aria-labelledby="ids.courseLength"
                    :aria-invalid="shouldShowError('courseLength')"
                  >
                    <button
                      v-for="option in courseLengthOptions"
                      :key="option.value"
                      type="button"
                      :class="['pill-button', { active: selections.courseLength === option.value }]"
                      :data-test="'length-' + option.value"
                      @click="selectOne('courseLength', option.value)"
                    >
                      {{ $t('courseSetup.courseLengthOptions.' + option.value) }}
                    </button>
                  </div>
                  <p v-if="shouldShowError('courseLength')" class="error-message" role="alert">
                    {{ $t('courseSetup.errors.courseLength') }}
                  </p>
                </div>
                <div class="input-section">
                  <label :id="ids.lessonsPerWeek">{{ $t('courseSetup.lessonsPerWeekLabel') }}</label>
                  <div
                    class="pill-group"
                    role="group"
                    :aria-labelledby="ids.lessonsPerWeek"
                    :aria-invalid="shouldShowError('lessonsPerWeek')"
                  >
                    <button
                      v-for="option in lessonsPerWeekOptions"
                      :key="option.value"
                      type="button"
                      :class="['pill-button', { active: selections.lessonsPerWeek === option.value }]"
                      :data-test="'perweek-' + option.value"
                      @click="selectOne('lessonsPerWeek', option.value)"
                    >
                      {{ option.value }}
                    </button>
                  </div>
                  <p v-if="shouldShowError('lessonsPerWeek')" class="error-message" role="alert">
                    {{ $t('courseSetup.errors.lessonsPerWeek') }}
                  </p>
                </div>
                <div class="input-section">
                  <label :id="ids.lessonDuration">{{ $t('courseSetup.lessonDurationLabel') }}</label>
                  <div
                    class="pill-group"
                    role="group"
                    :aria-labelledby="ids.lessonDuration"
                    :aria-invalid="shouldShowError('lessonDuration')"
                  >
                    <button
                      v-for="option in durationOptions"
                      :key="option.value"
                      type="button"
                      :class="['pill-button', { active: selections.lessonDurationMinutes === option.value }]"
                      :data-test="'duration-' + option.value"
                      @click="selectOne('lessonDurationMinutes', option.value)"
                    >
                      {{ $t('courseSetup.durationOptions.' + option.value) }}
                    </button>
                  </div>
                  <p v-if="shouldShowError('lessonDuration')" class="error-message" role="alert">
                    {{ $t('courseSetup.errors.lessonDuration') }}
                  </p>
                </div>
                <div v-if="scheduleSummary" class="schedule-summary" data-test="schedule-summary">
                  <font-awesome-icon icon="calendar-check" class="summary-icon" />
                  {{ scheduleSummary }}
                </div>
              </template>

              <!-- Step 3: Learning style -->
              <template v-else-if="currentStep === 3">
                <h3 ref="stepTitle" tabindex="-1" class="step-title">
                  🎓 {{ $t('courseSetup.steps.style') }}
                </h3>
                <div class="input-section">
                  <label :id="ids.preferredMethods">{{ $t('courseSetup.learningMethodsLabel') }}</label>
                  <div
                    class="pill-group"
                    role="group"
                    :aria-labelledby="ids.preferredMethods"
                    :aria-invalid="shouldShowError('preferredMethods')"
                  >
                    <button
                      v-for="method in learningMethods"
                      :key="method.value"
                      type="button"
                      :class="['pill-button', { active: selections.preferredMethods.includes(method.value) }]"
                      :data-test="'method-' + method.value"
                      @click="toggleMulti(selections.preferredMethods, method.value)"
                    >
                      {{ $t('courseSetup.learningMethods.' + method.value) }}
                    </button>
                  </div>
                  <p v-if="shouldShowError('preferredMethods')" class="error-message" role="alert">
                    {{ $t('courseSetup.errors.methods') }}
                  </p>
                </div>
                <div class="input-section">
                  <label :id="ids.skillFocus">{{ $t('courseSetup.skillFocusLabel') }}</label>
                  <div
                    class="pill-group"
                    role="group"
                    :aria-labelledby="ids.skillFocus"
                    :aria-invalid="shouldShowError('skillFocus')"
                  >
                    <button
                      v-for="skill in skillFocusOptions"
                      :key="skill.value"
                      type="button"
                      :class="['pill-button', { active: selections.skillFocus === skill.value }]"
                      :data-test="'skill-' + skill.value"
                      @click="selectOne('skillFocus', skill.value)"
                    >
                      {{ $t('courseSetup.skillFocusOptions.' + skill.value) }}
                    </button>
                  </div>
                  <p v-if="shouldShowError('skillFocus')" class="error-message" role="alert">
                    {{ $t('courseSetup.errors.skillFocus') }}
                  </p>
                </div>
              </template>

              <!-- Step 4: About you -->
              <template v-else>
                <h3 ref="stepTitle" tabindex="-1" class="step-title">
                  💡 {{ $t('courseSetup.steps.personalize') }}
                </h3>
                <div class="input-section">
                  <label :id="ids.previousExperience">{{ $t('courseSetup.previousExperienceLabel') }}</label>
                  <div
                    class="pill-group"
                    role="group"
                    :aria-labelledby="ids.previousExperience"
                    :aria-invalid="shouldShowError('previousExperience')"
                  >
                    <button
                      v-for="exp in previousExperienceOptions"
                      :key="exp.value"
                      type="button"
                      :class="['pill-button', { active: selections.previousExperience === exp.value }]"
                      :data-test="'experience-' + exp.value"
                      @click="selectOne('previousExperience', exp.value)"
                    >
                      {{ $t('courseSetup.previousExperienceOptions.' + exp.value) }}
                    </button>
                  </div>
                  <p v-if="shouldShowError('previousExperience')" class="error-message" role="alert">
                    {{ $t('courseSetup.errors.experience') }}
                  </p>
                </div>
                <div class="input-section">
                  <label :id="ids.challenges">{{ $t('courseSetup.challengesLabel') }}</label>
                  <div class="pill-group" role="group" :aria-labelledby="ids.challenges">
                    <button
                      v-for="challenge in challengesOptions"
                      :key="challenge.value"
                      type="button"
                      :class="['pill-button', { active: selections.challenges.includes(challenge.value) }]"
                      :data-test="'challenge-' + challenge.value"
                      @click="toggleChallenge(challenge.value)"
                    >
                      {{ $t('courseSetup.challengesOptions.' + challenge.value) }}
                    </button>
                  </div>
                </div>
                <div class="input-section">
                  <label :for="ids.culturalContext">{{ $t('courseSetup.culturalContextLabel') }}</label>
                  <textarea
                    :id="ids.culturalContext"
                    v-model="selections.culturalContext"
                    class="text-input"
                    rows="3"
                    maxlength="200"
                    :placeholder="$t('courseSetup.culturalContextPlaceholder')"
                  ></textarea>
                </div>
              </template>
            </div>
          </Transition>

          <footer class="wizard-footer">
            <!-- Optimistic mount: the create failed after the wizard was
                 already on screen. Retry is offered only when the failure can
                 actually succeed on a second attempt (a 403 cannot). -->
            <div
              v-if="createState === 'error'"
              class="create-error"
              data-test="wizard-create-error"
              role="alert"
            >
              <p class="create-error-text">
                {{ $t('courseSetup.skipFailed') }}
              </p>
              <button
                v-if="createRetryable"
                class="retry-btn"
                data-test="wizard-create-retry"
                type="button"
                @click="$emit('retry-create')"
              >
                {{ $t('courseSetup.retry') }}
              </button>
            </div>
            <p v-if="submitError" class="error-message submit-error" role="alert">
              {{ $t(submitErrorKey) }}
            </p>
            <button
              class="cta-btn"
              :class="{ 'cta-final': currentStep === totalSteps }"
              data-test="wizard-cta"
              type="button"
              :disabled="ctaBusy"
              @click="onCta"
            >
              <span v-if="ctaBusy" class="spinner-border spinner-border-sm me-2"></span>
              {{ currentStep === totalSteps ? $t('courseSetup.buildCourse') : $t('common.continue') }}
            </button>
            <button
              v-if="currentStep === 1"
              class="chat-instead-btn"
              data-test="wizard-chat-instead"
              type="button"
              :disabled="!isCreateReady"
              @click="onChatInstead"
            >
              <span v-if="!isCreateReady" class="spinner-border spinner-border-sm me-2"></span>
              {{ $t('courseSetup.chatInstead') }}
            </button>
          </footer>
        </template>
      </div>
    </div>
  </Teleport>
</template>

<script>
import { nextTick } from "vue";
import { authStore } from "@/store/auth";
import { cloudFunctions } from "@/store/cloudFunctions";
import { useHaptics } from "@/composables/useHaptics.js";
import { courseSetupDraftKey } from "@/scripts/courseSetupRouting.js";
import {
  isRateLimited,
  readRefusal,
  refusalMessageKey,
} from "@/scripts/continueChatErrors.js";

// Course-setup wizard: full-screen stepped flow on mobile, centered card on
// desktop. Replaces the old single-page modal that lived inside the chat's
// first message — this component is mounted by LessonsView directly from the
// COLLECT_BACKGROUND course stage, so no chat/dialog message is involved.
//
const TOTAL_STEPS = 4;
// How long the success moment stays on screen before handing back to the
// course checklist. The unit test advances fake timers past this.
const SUCCESS_DISPLAY_MS = 1600;
// Free-text draft fields are clamped to this on restore (mirrors the
// textarea maxlength, which doesn't apply to programmatic writes).
const MAX_TEXT_LENGTH = 200;
// The inline submit error's default wording: something went wrong and the
// learner should try the button again. It covers a network failure, an expired
// session, a 500, and every refusal this build has no specific copy for.
const SAVE_FAILED_KEY = "courseSetup.saveFailed";

// Which message a refused submit shows, or null to keep the default.
//
// `courseSetupBusy` is the 503 both `lessonsApi` and, since the turn bounds,
// `continueChat`'s submitStructuredData answer when another in-flight request
// holds this course's setup claim. It is a wait-and-retry state rather than a
// failure, so it gets its own copy on whichever endpoint returned it.
//
// The rate limit is the only OTHER refusal that gets specific copy here. The
// turn ceiling is deliberately left on the default message, for the same reason
// LessonsView leaves it there: "start a new one to keep going" is advice this
// screen gives no way to follow, since nothing here can start a second
// course-background dialog. submitStructuredData takes no reservation of its
// own, but continueChat consults the denied-dialog cache above the
// COURSE_BACKGROUND branch, so a background dialog whose skipStructuredInput
// already hit the ceiling can answer 409 here for up to one cache window.
function submitErrorKeyFor(code) {
  if (code === "courseSetupBusy") return "courseSetup.busyRetry";
  return isRateLimited(code) ? refusalMessageKey(code) : null;
}

export default {
  name: "CourseSetupWizard",
  props: {
    // Nullable on purpose: LessonsView mounts this component the instant
    // Start is tapped and feeds the id in
    // when the server create resolves. Everything id-dependent (draft
    // persistence, submit, chat-instead) is gated on `createState`.
    dialogId: { type: String, default: null },
    // pending | ready | error — the parent's course-create state machine.
    // Defaults to ready for direct mounts with a known dialog id.
    createState: { type: String, default: "ready" },
    // Whether the `error` state can be resolved by re-issuing the request. A
    // 403 languageNotEntitled cannot, and a Retry button there would just be
    // a loop that always lands back on the same banner.
    createRetryable: { type: Boolean, default: true },
  },
  emits: ["completed", "chat-instead", "close", "retry-create"],
  data() {
    return {
      currentStep: 1,
      stepTransition: "step-next",
      // idle | saving | success
      phase: "idle",
      submitError: false,
      // Which message the inline submit error shows. continueChat can now
      // refuse a submission for two reasons the learner recovers from by
      // waiting rather than by retrying differently, so the wording is a
      // locale key rather than the one fixed string it used to be.
      submitErrorKey: SAVE_FAILED_KEY,
      // Any selection made in THIS session. When the dialog id lands late it
      // may belong to an ADOPTED dialog carrying a previous session's draft;
      // restoring over live answers (and snapping the step forward with them)
      // is worse than dropping the draft, so this coarse flag vetoes it.
      sessionDirty: false,
      // Tracks which steps the user tried to advance past, so validation
      // errors only show after an attempt (per step, not globally).
      attemptedSteps: [],
      // Debounce handle for draft autosave; suppressed after clearDraft so a
      // trailing tick can't resurrect a deliberately cleared draft.
      draftTimer: null,
      draftSuppressed: false,
      successTimer: null,
      // Modality bookkeeping: element to restore focus to and the body
      // overflow value to put back when the wizard closes.
      previouslyFocused: null,
      prevBodyOverflow: "",
      selections: {
        specificGoals: [],
        specificGoalsDetails: "",
        courseLength: null,
        lessonsPerWeek: null,
        lessonDurationMinutes: null,
        preferredMethods: [],
        skillFocus: null,
        previousExperience: null,
        challenges: ["none"],
        culturalContext: "",
      },
      // Option keys mirror backend maps in InfoGettingAgent.py / Lessons.py
      // (enforced by ConstantsSyncTest). Labels come from locale files.
      specificGoalsOptions: [
        { value: "travel" },
        { value: "work" },
        { value: "academic" },
        { value: "media" },
        { value: "medical" },
        { value: "family" },
        { value: "relocation" },
        { value: "hobby" },
        { value: "other" },
      ],
      // weeks feeds the live schedule summary; keeping it on the synced
      // option entries means a new course length can't miss it silently.
      courseLengthOptions: [
        { value: "2_weeks", weeks: 2 },
        { value: "1_month", weeks: 4 },
        { value: "2_months", weeks: 8 },
        { value: "3_months", weeks: 12 },
      ],
      // Keep in sync with Lessons.LESSONS_PER_WEEK_OPTIONS (billing cap of 3).
      lessonsPerWeekOptions: [{ value: 1 }, { value: 2 }, { value: 3 }],
      // Keep in sync with Lessons.LESSON_DURATION_OPTIONS.
      durationOptions: [{ value: 5 }, { value: 10 }, { value: 15 }],
      learningMethods: [
        { value: "conversation" },
        { value: "grammar" },
        { value: "multimedia" },
        { value: "reading" },
        { value: "exercises" },
      ],
      skillFocusOptions: [
        { value: "speaking" },
        { value: "listening" },
        { value: "both" },
      ],
      previousExperienceOptions: [
        { value: "none" },
        { value: "self_study" },
        { value: "classes" },
        { value: "immersion" },
        { value: "mixed" },
      ],
      challengesOptions: [
        { value: "none" },
        { value: "pronunciation" },
        { value: "grammar" },
        { value: "vocabulary" },
        { value: "confidence" },
        { value: "time" },
        { value: "consistency" },
        { value: "other" },
      ],
      ids: {
        specificGoals: "wizard-goals-label",
        goalsDetails: "wizard-goals-details",
        courseLength: "wizard-length-label",
        lessonsPerWeek: "wizard-perweek-label",
        lessonDuration: "wizard-duration-label",
        preferredMethods: "wizard-methods-label",
        skillFocus: "wizard-skill-label",
        previousExperience: "wizard-experience-label",
        challenges: "wizard-challenges-label",
        culturalContext: "wizard-context-input",
      },
    };
  },
  computed: {
    totalSteps() {
      return TOTAL_STEPS;
    },
    draftKey() {
      return courseSetupDraftKey(this.dialogId);
    },
    isCreateReady() {
      return this.createState === "ready";
    },
    // The final CTA is the only step that needs the dialog id: it POSTs the
    // payload. Disabling it (rather than awaiting inside submitSelections)
    // keeps `phase` at idle, so close and Escape keep working while the
    // create is in flight.
    ctaBusy() {
      return (
        this.phase === "saving" ||
        (this.currentStep === TOTAL_STEPS && !this.isCreateReady)
      );
    },
    scheduleSummary() {
      const { courseLength, lessonsPerWeek, lessonDurationMinutes } = this.selections;
      if (!courseLength || !lessonsPerWeek || !lessonDurationMinutes) return null;
      const weeks = this.courseLengthOptions.find(
        (o) => o.value === courseLength
      )?.weeks;
      if (!weeks) return null;
      return this.$t("courseSetup.scheduleSummary", {
        lessons: weeks * lessonsPerWeek,
        minutes: lessonDurationMinutes,
      });
    },
    fieldValidity() {
      const s = this.selections;
      return {
        specificGoals: s.specificGoals.length > 0,
        specificGoalsDetails:
          !s.specificGoals.includes("other") ||
          s.specificGoalsDetails.trim().length > 0,
        courseLength: !!s.courseLength,
        lessonsPerWeek: !!s.lessonsPerWeek,
        lessonDuration: !!s.lessonDurationMinutes,
        preferredMethods: s.preferredMethods.length > 0,
        skillFocus: !!s.skillFocus,
        previousExperience: !!s.previousExperience,
      };
    },
    stepFields() {
      return {
        1: ["specificGoals", "specificGoalsDetails"],
        2: ["courseLength", "lessonsPerWeek", "lessonDuration"],
        3: ["preferredMethods", "skillFocus"],
        4: ["previousExperience"],
      };
    },
  },
  watch: {
    selections: {
      deep: true,
      handler() {
        this.sessionDirty = true;
        // Debounced: the textareas fire this per keystroke, and a synchronous
        // localStorage write per keypress is measurable jank on low-end phones.
        this.scheduleDraftSave();
      },
    },
    // The optimistic mount's late-id path. Registered AFTER the selections
    // watcher on purpose: Vue runs pre-flush watchers in registration order,
    // so a selection made in the same tick as the id arriving has already set
    // sessionDirty by the time this runs.
    dialogId(newId, oldId) {
      // Only the null -> id transition; the id never changes once set.
      if (!newId || oldId) return;
      if (!this.sessionDirty) this.restoreDraft();
      // Start persisting: everything typed before the id existed had no key
      // to be written under.
      this.saveDraft();
    },
  },
  mounted() {
    this.restoreDraft();
    // aria-modal promises real modality: hold focus inside the card and stop
    // the page underneath from scrolling while the wizard is open.
    this.previouslyFocused = document.activeElement;
    this.prevBodyOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    nextTick(() => this.$refs.card?.focus({ preventScroll: true }));
  },
  beforeUnmount() {
    clearTimeout(this.draftTimer);
    clearTimeout(this.successTimer);
    this.saveDraft();
    document.body.style.overflow = this.prevBodyOverflow || "";
    if (typeof this.previouslyFocused?.focus === "function") {
      this.previouslyFocused.focus();
    }
  },
  methods: {
    onEscape() {
      // Escape mirrors the back chevron: previous step, or close on step 1.
      // Ignored while saving / celebrating so it can't interrupt the submit.
      if (this.phase !== "idle") return;
      this.goBack();
    },
    // Same wrap-within-container pattern as BottomSheet.trapFocus.
    trapFocus(event) {
      const items = Array.from(
        this.$refs.card?.querySelectorAll(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
        ) || []
      ).filter((el) => !el.disabled && el.offsetParent !== null);
      if (items.length === 0) {
        event.preventDefault();
        return;
      }
      const first = items[0];
      const last = items[items.length - 1];
      if (!items.includes(document.activeElement)) {
        event.preventDefault();
        (event.shiftKey ? last : first).focus();
      } else if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    },
    // DESIGN.md mandates a light impact on a completed selection. Every pill
    // tap in all eight groups routes through one of the three methods below so
    // the feedback can't drift apart per group.
    pillFeedback() {
      useHaptics().impactLight();
    },
    // Single-select groups. Kept as a method (rather than an inline assignment
    // in the template) so the option arrays stay the only `value:` literals in
    // the file for ConstantsSyncTest's regex parse.
    selectOne(field, value) {
      this.selections[field] = value;
      this.pillFeedback();
    },
    // Multi-select groups (goals, learning methods). `toggleIn` itself stays a
    // pure list mutator because `toggleChallenge` reuses it and fires its own
    // feedback — putting the haptic in there would double-fire.
    toggleMulti(list, value) {
      this.toggleIn(list, value);
      this.pillFeedback();
    },
    toggleIn(list, value) {
      const index = list.indexOf(value);
      if (index > -1) {
        list.splice(index, 1);
        if (value === "other" && list === this.selections.specificGoals) {
          this.selections.specificGoalsDetails = "";
        }
      } else {
        list.push(value);
      }
    },
    toggleChallenge(value) {
      const challenges = this.selections.challenges;
      this.pillFeedback();
      if (value === "none") {
        this.selections.challenges = ["none"];
        return;
      }
      const noneIndex = challenges.indexOf("none");
      if (noneIndex > -1) challenges.splice(noneIndex, 1);
      this.toggleIn(challenges, value);
      if (challenges.length === 0) this.selections.challenges = ["none"];
    },
    isStepValid(step) {
      return this.stepFields[step].every((f) => this.fieldValidity[f]);
    },
    shouldShowError(field) {
      const step = Object.keys(this.stepFields).find((s) =>
        this.stepFields[s].includes(field)
      );
      return this.attemptedSteps.includes(Number(step)) && !this.fieldValidity[field];
    },
    focusStepTitle() {
      nextTick(() => this.$refs.stepTitle?.focus({ preventScroll: true }));
    },
    goBack() {
      // Mirror onEscape's guard: the back chevron must not interrupt an
      // in-flight submit or the success animation.
      if (this.phase !== "idle") return;
      if (this.currentStep === 1) {
        this.$emit("close");
        return;
      }
      this.stepTransition = "step-prev";
      this.currentStep -= 1;
      this.saveDraft();
      this.focusStepTitle();
    },
    onCta() {
      if (!this.isStepValid(this.currentStep)) {
        if (!this.attemptedSteps.includes(this.currentStep)) {
          this.attemptedSteps.push(this.currentStep);
        }
        return;
      }
      if (this.currentStep < TOTAL_STEPS) {
        useHaptics().impactLight();
        this.stepTransition = "step-next";
        this.currentStep += 1;
        // Advancing is itself session progress. Today it implies a selection
        // (isStepValid gates it, and every step's fields start empty), so the
        // selections watcher has already flagged it — but the late-id restore
        // must never be able to yank a learner off the step they are reading
        // just because a future step gains a valid default.
        this.sessionDirty = true;
        this.saveDraft();
        this.focusStepTitle();
        return;
      }
      this.submitSelections();
    },
    async submitSelections() {
      this.phase = "saving";
      this.submitError = false;
      this.submitErrorKey = SAVE_FAILED_KEY;
      try {
        const token = await authStore.getIdToken();
        const response = await fetch(cloudFunctions.getContinueChatApi(), {
          method: "POST",
          headers: {
            Authorization: "Bearer " + token,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            dialogId: this.dialogId,
            structuredData: this.selections,
            action: "submitStructuredData",
          }),
        });
        if (!response.ok) {
          // One read of the body serves both jobs: the bare-string match that
          // recognises the benign "already complete" 400, and the JSON error
          // code the turn bounds answer a refusal with. A fetch body can be
          // read only once, so it cannot be two separate reads.
          const { code, text } = await readRefusal(response);
          // A dialog completed elsewhere (double submit, second device) means
          // the data is already collected — let the parent refresh, don't error.
          if (!(response.status === 400 && text.includes("already complete"))) {
            const error = new Error(`HTTP ${response.status}`);
            error.submitErrorKey = submitErrorKeyFor(code);
            throw error;
          }
        }
        this.clearDraft();
        this.phase = "success";
        useHaptics().notifySuccess();
        this.successTimer = setTimeout(
          () => this.$emit("completed"),
          SUCCESS_DISPLAY_MS
        );
      } catch (error) {
        console.error("Failed to submit course setup:", error);
        this.phase = "idle";
        this.submitError = true;
        // Null for a network failure, an auth failure, or any refusal with no
        // specific copy, and the generic message is right for all of those.
        this.submitErrorKey = error?.submitErrorKey || SAVE_FAILED_KEY;
      }
    },
    onChatInstead() {
      // Deliberately keep the draft: if the switch to chat fails, the parent
      // remounts this wizard and the learner's answers must survive. The
      // parent removes the draft once the switch succeeds.
      this.$emit("chat-instead");
    },
    scheduleDraftSave() {
      clearTimeout(this.draftTimer);
      this.draftTimer = setTimeout(() => this.saveDraft(), 400);
    },
    saveDraft() {
      clearTimeout(this.draftTimer);
      // Never resurrect a draft that was deliberately cleared (post-submit /
      // chat-instead) — a trailing debounce tick or unmount flush could
      // otherwise re-write it after clearDraft().
      if (this.draftSuppressed) return;
      // No id yet (optimistic mount): there is no key to write under. The
      // guard lives HERE rather than at the call sites because goBack and
      // onCta's step advance both save during the pre-id window, and
      // localStorage.setItem would coerce a null key to the string "null" —
      // one shared bucket leaking answers between sessions.
      //
      // ACCEPTED CONSEQUENCE: answers given before the id arrives are lost if
      // the learner closes during that window (~1-3s), including the
      // close-then-retap path, which remounts an empty wizard. A provisional
      // key was considered and rejected: `courseSetupDraft:null` leaks one
      // learner's session into the next, which is a correctness bug rather
      // than a lost second of typing.
      if (!this.draftKey) return;
      try {
        localStorage.setItem(
          this.draftKey,
          JSON.stringify({ step: this.currentStep, selections: this.selections })
        );
      } catch (e) {
        // Storage full/unavailable: the draft is a convenience, never fatal.
      }
    },
    restoreDraft() {
      if (!this.draftKey) return;
      try {
        const raw = localStorage.getItem(this.draftKey);
        if (!raw) return;
        const draft = JSON.parse(raw);
        Object.assign(this.selections, this.sanitizeDraftSelections(draft?.selections));
        if (
          Number.isInteger(draft?.step) &&
          draft.step >= 1 &&
          draft.step <= TOTAL_STEPS
        ) {
          // Never restore past an incomplete step: sanitization may have
          // dropped fields (changed option keys, tampered draft), and onCta
          // only validates the current step — landing on step 4 with an
          // empty step 2 would allow submitting an incomplete payload.
          this.currentStep = this.firstIncompleteStepUpTo(draft.step);
        }
      } catch (e) {
        // Corrupt draft: start fresh.
      }
    },
    firstIncompleteStepUpTo(step) {
      for (let s = 1; s < step; s++) {
        if (!this.isStepValid(s)) return s;
      }
      return step;
    },
    // localStorage is user-writable, so a restored draft is untrusted input:
    // every field is validated against the known option keys (or clamped, for
    // free text) before it can reach the submission payload.
    sanitizeDraftSelections(saved) {
      if (!saved || typeof saved !== "object") return {};
      const values = (options) => options.map((o) => o.value);
      const pickMulti = (value, options) =>
        Array.isArray(value) ? value.filter((v) => options.includes(v)) : undefined;
      const pickOne = (value, options) =>
        options.includes(value) ? value : undefined;
      const pickText = (value) =>
        typeof value === "string" ? value.slice(0, MAX_TEXT_LENGTH) : undefined;
      const clean = {
        specificGoals: pickMulti(saved.specificGoals, values(this.specificGoalsOptions)),
        specificGoalsDetails: pickText(saved.specificGoalsDetails),
        courseLength: pickOne(saved.courseLength, values(this.courseLengthOptions)),
        lessonsPerWeek: pickOne(saved.lessonsPerWeek, values(this.lessonsPerWeekOptions)),
        lessonDurationMinutes: pickOne(saved.lessonDurationMinutes, values(this.durationOptions)),
        preferredMethods: pickMulti(saved.preferredMethods, values(this.learningMethods)),
        skillFocus: pickOne(saved.skillFocus, values(this.skillFocusOptions)),
        previousExperience: pickOne(saved.previousExperience, values(this.previousExperienceOptions)),
        challenges: pickMulti(saved.challenges, values(this.challengesOptions)),
        culturalContext: pickText(saved.culturalContext),
      };
      Object.keys(clean).forEach((key) => {
        if (clean[key] === undefined) delete clean[key];
      });
      if (clean.challenges) {
        // Preserve the none-is-exclusive invariant the pill UI enforces.
        if (clean.challenges.includes("none") && clean.challenges.length > 1) {
          clean.challenges = clean.challenges.filter((c) => c !== "none");
        }
        if (clean.challenges.length === 0) delete clean.challenges;
      }
      return clean;
    },
    clearDraft() {
      clearTimeout(this.draftTimer);
      // Suppression is set before the id guard on purpose: it must hold even
      // if the id lands afterwards, or the late-id watcher would resurrect a
      // deliberately cleared draft.
      this.draftSuppressed = true;
      if (!this.draftKey) return;
      try {
        localStorage.removeItem(this.draftKey);
      } catch (e) {
        // Ignore: nothing to clean up if storage is unavailable.
      }
    },
  },
};
</script>

<style scoped>
.setup-wizard {
  position: fixed;
  inset: 0;
  /* Above the navigation layer, matching Modal.vue / AddLanguageSteps. */
  z-index: 2150;
  display: flex;
  flex-direction: column;
  /* Plain surface (not the tinted page background) so the sunken pill fills
     stay visible on the mobile full-screen layout. */
  background: var(--al-surface);
}

.wizard-card {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  width: 100%;
}

/* Programmatic focus target on open (tabindex="-1"): no visible ring. */
.wizard-card:focus {
  outline: none;
}

/* Desktop: scrim + centered card instead of a full-screen takeover. */
@media (min-width: 768px) {
  .setup-wizard {
    background: var(--al-scrim);
    align-items: center;
    justify-content: center;
    padding: 32px 16px;
  }

  .wizard-card {
    flex: 0 1 auto;
    max-width: 560px;
    max-height: 90vh;
    background: var(--al-surface-raised);
    border-radius: 22px;
    overflow: hidden;
    box-shadow: 0 24px 64px rgba(8, 26, 49, 0.35);
  }
}

/* Header: deep navy band (hue-fixed brand color, light text in both themes). */
.wizard-header {
  flex-shrink: 0;
  background: linear-gradient(160deg, #0c223c 0%, #0f3354 100%);
  color: #f7fafd;
  padding: max(8px, env(safe-area-inset-top, 0px)) 20px 16px;
}

@media (min-width: 768px) {
  .wizard-header {
    padding-top: 12px;
  }
}

.header-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-left: -10px;
}

.back-btn {
  width: 44px;
  height: 44px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  border: none;
  border-radius: 50%;
  color: #f7fafd;
  font-size: 18px;
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
}

/* Hover only where hover is real: on touch WebViews :hover latches onto the
   last-tapped element until the next tap elsewhere, so an unguarded rule keeps
   painting a control as hovered long after the finger has left it. Gating on
   the PRIMARY pointer (not `any-hover`) is deliberate: a touch-first hybrid
   with a mouse attached would otherwise get the latch back, and losing a hover
   tint there is the cheaper of the two failures. */
@media (hover: hover) and (pointer: fine) {
  .back-btn:hover {
    background: rgba(247, 250, 253, 0.08);
  }
}

/* Press feedback stays unguarded — on touch it is the only tap affordance. */
.back-btn:active {
  background: rgba(247, 250, 253, 0.12);
}

.step-label {
  font-size: 12px;
  font-weight: 650;
  letter-spacing: 0.04em;
  color: #8ca0b3;
}

.wizard-title {
  font-family: "Fraunces", Georgia, serif;
  font-size: 26px;
  font-weight: 700;
  line-height: 1.16;
  margin: 2px 0 4px;
}

.wizard-intro {
  font-size: 14px;
  line-height: 1.45;
  color: #d8e2ec;
  margin: 0 0 16px;
}

.progress-track {
  display: flex;
  gap: 8px;
}

.progress-segment {
  flex: 1;
  height: 4px;
  border-radius: 999px;
  background: rgba(247, 250, 253, 0.18);
  transition: background-color var(--al-dur-medium, 300ms) var(--al-ease-move, ease);
}

.progress-segment.filled {
  background: var(--al-primary, #2d90eb);
}

.step-body {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: 20px 20px 8px;
}

.step-title {
  font-size: 20px;
  font-weight: 700;
  line-height: 1.24;
  color: var(--al-text);
  margin: 0 0 16px;
}

/* Programmatic focus (step transitions) stays quiet; a keyboard-visible
   focus keeps the default indicator. */
.step-title:focus:not(:focus-visible) {
  outline: none;
}

.input-section {
  margin-bottom: 24px;
}

.input-section label {
  display: block;
  font-size: 16px;
  font-weight: 600;
  color: var(--al-text);
  margin-bottom: 8px;
}

.pill-group {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.pill-button {
  display: inline-flex;
  align-items: center;
  min-height: 44px;
  padding: 8px 16px;
  background-color: var(--al-surface-sunken);
  border: 2px solid transparent;
  border-radius: var(--al-radius-pill, 999px);
  font-size: 16px;
  color: var(--al-text);
  cursor: pointer;
  /* Split out of `transition: all` so the press scale can run on the micro
     duration while the fill/border keep the short one. The selected pill's
     font-weight step is deliberately left out: it is the one other property
     `all` used to cover, and browsers snap between static weights anyway. */
  transition: background-color var(--al-dur-short, 180ms) var(--al-ease-move, ease),
    border-color var(--al-dur-short, 180ms) var(--al-ease-move, ease),
    transform var(--al-dur-micro, 80ms) var(--al-ease-move, ease);
  -webkit-tap-highlight-color: transparent;
}

/* Capability-guarded (see .back-btn:hover above): a latched hover on touch was
   leaving a *deselected* pill blue, since the hover fill is more saturated than
   the selected fill in both themes. `:not(.active)` additionally makes this
   rule mutually exclusive with `.pill-button.active` — the two used to tie at
   specificity (0,2,0) and depend on source order, which the production CSS
   minifier is documented to shuffle for duplicate equal-specificity selectors
   (CHANGELOG, bug #573). */
@media (hover: hover) and (pointer: fine) {
  .pill-button:hover:not(.active) {
    background-color: var(--al-primary-soft-hover, var(--al-border));
  }
}

/* Press feedback for selecting *and* deselecting taps. */
.pill-button:active {
  transform: scale(0.98);
}

.pill-button.active {
  background-color: var(--al-primary-soft);
  border-color: var(--al-primary);
  font-weight: 600;
}

.text-input {
  width: 100%;
  padding: 12px 16px;
  background-color: var(--al-surface-sunken);
  border: 1px solid var(--al-border);
  border-radius: var(--al-radius, 12px);
  /* 16px floor: anything smaller triggers iOS Safari's zoom-on-focus. */
  font-size: 16px;
  font-family: inherit;
  color: var(--al-text);
  resize: vertical;
}

.text-input:focus {
  outline: none;
  border-color: var(--al-primary);
  box-shadow: 0 0 0 3px var(--al-primary-soft);
}

.text-input::placeholder {
  color: var(--al-text-muted);
}

.error-message {
  font-size: 14px;
  color: var(--al-danger, #dc3545);
  margin: 8px 0 0;
}

.schedule-summary {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 16px;
  background-color: var(--al-primary-soft);
  border-radius: var(--al-radius, 12px);
  font-size: 16px;
  font-weight: 600;
  color: var(--al-text);
}

.summary-icon {
  color: var(--al-primary);
}

.wizard-footer {
  flex-shrink: 0;
  padding: 12px 20px max(16px, env(safe-area-inset-bottom, 0px));
}

.submit-error {
  margin-bottom: 10px;
}

/* Course-create failure banner (optimistic mount). Sits above the CTA so the
   learner reads the failure before the disabled Build button. Uses the
   danger-bg/border/text trio (not the raw --al-danger red) because those are
   the tokens with a dark-mode override. */
.create-error {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 12px;
  padding: 12px 16px;
  border: 1px solid var(--al-danger-border);
  border-radius: var(--al-radius-md, 12px);
  background-color: var(--al-danger-bg);
}

.create-error-text {
  flex: 1;
  margin: 0;
  font-size: 14px;
  color: var(--al-danger-text);
}

.retry-btn {
  flex-shrink: 0;
  min-height: 44px;
  padding: 8px 16px;
  background: transparent;
  border: 1px solid var(--al-danger-border);
  border-radius: var(--al-radius-sm, 8px);
  font-size: 14px;
  font-weight: 600;
  color: var(--al-danger-text);
  cursor: pointer;
  transition: background-color var(--al-dur-short, 180ms) var(--al-ease-move, ease);
}

@media (hover: hover) and (pointer: fine) {
  .retry-btn:hover {
    background-color: var(--al-danger-border);
  }
}

.retry-btn:active {
  background-color: var(--al-danger-border);
}

.cta-btn {
  width: 100%;
  min-height: 52px;
  background-color: var(--al-primary);
  color: var(--al-white, #fff);
  border: none;
  border-radius: var(--al-radius, 12px);
  font-size: 17px;
  font-weight: 600;
  cursor: pointer;
  transition: background-color var(--al-dur-short, 180ms) var(--al-ease-move, ease);
}

@media (hover: hover) and (pointer: fine) {
  .cta-btn:hover:not(:disabled) {
    background-color: var(--al-primary-hover);
  }
}

.cta-btn:active:not(:disabled) {
  background-color: var(--al-primary-hover);
}

.cta-btn:disabled {
  opacity: 0.7;
  cursor: wait;
}

/* The final step's CTA is the celebratory moment: warm accent per DESIGN.md.
   Hover/pressed uses elevation (like .button.assessment) rather than
   introducing a new orange outside the palette. */
.cta-btn.cta-final {
  background-color: var(--al-orange, #eb882d);
  color: #081a31;
}

@media (hover: hover) and (pointer: fine) {
  .cta-btn.cta-final:hover:not(:disabled) {
    background-color: var(--al-orange, #eb882d);
    box-shadow: var(--al-shadow-md);
  }
}

.cta-btn.cta-final:active:not(:disabled) {
  background-color: var(--al-orange, #eb882d);
  box-shadow: var(--al-shadow-md);
}

.chat-instead-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  min-height: 44px;
  margin-top: 8px;
  padding: 8px;
  background: transparent;
  border: none;
  font-size: 14px;
  color: var(--al-text-muted);
  text-decoration: underline;
  text-underline-offset: 3px;
  cursor: pointer;
}

/* :not(:disabled) here for the same reason as .button.setup-continue in
   LessonsView: the prod CSS minifier reorders equal-specificity duplicate
   selectors, so :hover and :disabled must be mutually exclusive rather than
   order-dependent. */
@media (hover: hover) and (pointer: fine) {
  .chat-instead-btn:hover:not(:disabled) {
    color: var(--al-primary);
  }
}

.chat-instead-btn:disabled {
  opacity: 0.7;
  cursor: wait;
}

/* Success state */
.wizard-success {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 48px 32px;
}

.success-icon {
  width: 64px;
  height: 64px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  /* Icon-on-primary-soft is the DESIGN.md empty-state pairing; green on the
     blue soft fill fails the 3:1 non-text contrast guideline. */
  background-color: var(--al-primary-soft);
  color: var(--al-primary);
  font-size: 28px;
  margin-bottom: 20px;
  animation: success-pop 420ms var(--al-ease-enter, cubic-bezier(0.16, 1, 0.3, 1));
}

@keyframes success-pop {
  from {
    transform: scale(0.6);
    opacity: 0;
  }
  to {
    transform: scale(1);
    opacity: 1;
  }
}

.success-title {
  font-family: "Fraunces", Georgia, serif;
  font-size: 24px;
  font-weight: 700;
  color: var(--al-text);
  margin: 0 0 8px;
}

.success-body {
  font-size: 16px;
  line-height: 1.5;
  color: var(--al-text-muted);
  margin: 0;
  max-width: 340px;
}

/* Horizontal step transitions; reduced motion falls back to fade. */
.step-next-enter-active,
.step-next-leave-active,
.step-prev-enter-active,
.step-prev-leave-active {
  transition: transform 260ms var(--al-ease-move, cubic-bezier(0.4, 0, 0.2, 1)),
    opacity 260ms var(--al-ease-move, cubic-bezier(0.4, 0, 0.2, 1));
}

.step-next-enter-from,
.step-prev-leave-to {
  transform: translateX(32px);
  opacity: 0;
}

.step-prev-enter-from,
.step-next-leave-to {
  transform: translateX(-32px);
  opacity: 0;
}

@media (prefers-reduced-motion: reduce) {
  .step-next-enter-from,
  .step-prev-enter-from,
  .step-next-leave-to,
  .step-prev-leave-to {
    transform: none;
  }

  .success-icon {
    animation: none;
  }

  /* Press feedback is motion too: keep the state change, drop the scale. */
  .pill-button:active {
    transform: none;
  }
}
</style>
